import { useCallback, useMemo } from 'react';

import { EnhancedTab } from '@swyftx/aviary/molecules/EnhancedTabs/EnhancedTabs.types';
import { marginService } from '@swyftx/margin';
import { negativeTabValues, positiveTabValues } from '@swyftx/margin/build/consts';
import { MarginOrderDirection, MarginOrderType, ProtectionOrderVariant } from '@swyftx/margin/build/types';
import { MarginInstrument } from '@swyftx/margin/build/types';

import { Big } from '@shared/safe-big';

import { useMarginAccount } from 'src/lib/margin/hooks/account';
import { useMarginPrices } from 'src/lib/margin/hooks/prices';
import { useMarginProtectionOrderError } from 'src/lib/margin/hooks/trade/useMarginProtectionOrderError';
import { useMarkets } from 'src/lib/markets/hooks/useMarkets';
import { useFormatInputText } from 'src/lib/utils/hooks/useFormatInputText';

type Props = {
  tradeSide: MarginOrderDirection;
  tradeType: MarginOrderType;
  protectionOrderType: ProtectionOrderVariant;
  value?: string;
  lots?: string;
  initialChecked?: boolean;
  price?: string;
  positionMarket?: MarginInstrument;
  onChange: React.Dispatch<React.SetStateAction<string>>;
};

const useMarginPositionDetailsProtectionOrder = ({
  protectionOrderType,
  value = '',
  price = '',
  lots = '0',
  tradeSide,
  tradeType,
  positionMarket,
  onChange,
}: Props) => {
  const { getAssetByCode } = useMarkets();
  const { currency } = useMarginAccount();
  const { getMarketPrice } = useMarginPrices();

  const onUpdateValue = useCallback(
    (newPrice: string) => {
      onChange(newPrice);
    },
    [onChange],
  );

  const title = useMemo(() => {
    switch (protectionOrderType) {
      case ProtectionOrderVariant.TakeProfit:
        return 'Take profit';
      case ProtectionOrderVariant.StopLoss:
        return 'Stop loss';
      default:
        protectionOrderType satisfies never;
        return '';
    }
  }, [protectionOrderType]);

  const leadingLabel = useMemo(() => {
    switch (protectionOrderType) {
      case ProtectionOrderVariant.TakeProfit:
        return 'Take profit price';
      case ProtectionOrderVariant.StopLoss:
        return 'Stop loss price';
      default:
        protectionOrderType satisfies never;
        return '';
    }
  }, [protectionOrderType]);

  const { error } = useMarginProtectionOrderError({
    value,
    tradeSide,
    tradeType,
    protectionOrderType,
    price,
    selectedMarket: positionMarket,
  });

  const { formattedAmount, onChangeValue, priceScale } = useFormatInputText({
    asset: getAssetByCode('USD'),
    value,
    onChange: onUpdateValue,
    priceScaleOverride: positionMarket?.precision,
  });

  const tabs = useMemo((): EnhancedTab<string>[] => {
    const { isLong } = marginService.distilMarginInformation(tradeSide, tradeType);
    const isTakeProfit = protectionOrderType === ProtectionOrderVariant.TakeProfit;

    if ((isLong && isTakeProfit) || (!isLong && !isTakeProfit)) {
      return positiveTabValues.map((val) => ({
        value: val.value.toString(),
        title: val.label,
        animateColor: 'success',
      }));
    } else {
      return negativeTabValues.map((val) => ({
        value: val.value.toString(),
        title: val.label,
        animateColor: 'error',
      }));
    }
  }, [protectionOrderType, tradeSide, tradeType]);

  const onChangeTab = useCallback(
    (newValue: string) => {
      const bigValue = Big(Big(value).eq(0) ? price : value)
        .times(Big(1).plus(newValue))
        .toFixed(priceScale);

      onUpdateValue(bigValue.toString());
    },
    [value, price, priceScale, onUpdateValue],
  );

  const profitLossValue = useMemo(() => {
    if (!currency || !positionMarket || !value) return Big(0);

    const rate = getMarketPrice(`${currency?.code}${positionMarket.primaryAssetUuid}`);
    const side = tradeSide === MarginOrderDirection.Long ? 'ask' : 'bid';

    if (!rate) return Big(0);

    const audPrice = Big(price).times(rate[side]);

    return marginService
      .getProfitLossValue({
        lots: Number(lots),
        orderPrice: audPrice.toString(),
        contractSize: positionMarket.contractSize,
        protectionOrderPrice: value,
      })
      .abs();
  }, [currency, getMarketPrice, lots, positionMarket, price, tradeSide, value]);

  return {
    onChangeTab,
    tabs,
    onUpdateValue,
    value,
    title,
    leadingLabel,
    formattedAmount,
    profitLossValue,
    onChangeValue,
    error,
  };
};

export { useMarginPositionDetailsProtectionOrder };
